import { PageObject } from 'lib/HttpClient';
import { Option } from 'lib/types';

export interface ITrackingLink {
    internalTitle: string;
    type: TrackingLinkType;
    utmSource?: string;
    utmMedium?: string;
    utmCampaign?: string;
    utmContent?: string;
    utmTerm?: string;
    isArchived: boolean;
    clicks?: number;
    createdAt: string;
    updatedAt: string;
    shortLinkId: string;
    androidCustomUrl?: string;
    iosCustomUrl?: string;
    fallbackUrl: string;
    customQueryParameters?: ICustomQueryParam[];
    // used for web app links
    customPath?: string;
}

export interface ICustomQueryParam {
    key: CustomParamKeys;
    value: string;
}

export enum CustomParamKeys {
    LOCATION_ID = 'location_id',
    TABLE_NUMBER = 'table_number',
    DEEP_LINK = 'deep_link'
}
export const CustomParamKeysLabels = {
    [CustomParamKeys.LOCATION_ID]: 'Location ID',
    [CustomParamKeys.TABLE_NUMBER]: 'Table Number',
    [CustomParamKeys.DEEP_LINK]: 'Deep Link'
};

export enum DeepLinkDestinations {
    START_OF_JOURNEY = 'START_OF_JOURNEY',
    PRE_ORDER_MENU = 'PRE_ORDER_MENU',
    ORDER_TO_TABLE_MENU = 'ORDER_TO_TABLE_MENU',
    PAT_PAY_MENU = 'PAT_PAY_MENU',
    DINE_IN_MENU = 'DINE_IN_MENU',
    DELIVERY_MENU = 'DELIVERY_MENU',
    USER_DETAILS = 'USER_DETAILS',
    USER_REWARDS = 'USER_REWARDS',
    USER_ORDERS = 'USER_ORDERS',
    USER_PAYMENT_METHODS = 'USER_PAYMENT_METHODS'
}

export const DeepLinkDestinationsLabels = {
    [DeepLinkDestinations.START_OF_JOURNEY]: 'Start of journey',
    [DeepLinkDestinations.PRE_ORDER_MENU]: 'Preorder Menu',
    [DeepLinkDestinations.ORDER_TO_TABLE_MENU]: 'Order to Table Menu',
    [DeepLinkDestinations.PAT_PAY_MENU]: 'Pay at Table Menu',
    [DeepLinkDestinations.DINE_IN_MENU]: 'Dine In Menu',
    [DeepLinkDestinations.DELIVERY_MENU]: 'Delivery Menu',
    [DeepLinkDestinations.USER_DETAILS]: 'User Details ',
    [DeepLinkDestinations.USER_REWARDS]: 'User Rewards',
    [DeepLinkDestinations.USER_ORDERS]: 'User Orders',
    [DeepLinkDestinations.USER_PAYMENT_METHODS]: 'User Payment Methods'
};

export const DeepLinkDestinationsOptions: Option[] = [
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.START_OF_JOURNEY]} (default)`,
        value: DeepLinkDestinations.START_OF_JOURNEY
    },
    {
        label: DeepLinkDestinationsLabels[DeepLinkDestinations.PRE_ORDER_MENU],
        value: DeepLinkDestinations.PRE_ORDER_MENU
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.ORDER_TO_TABLE_MENU]}`,
        value: DeepLinkDestinations.ORDER_TO_TABLE_MENU
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.PAT_PAY_MENU]}`,
        value: DeepLinkDestinations.PAT_PAY_MENU
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.DINE_IN_MENU]}`,
        value: DeepLinkDestinations.DINE_IN_MENU
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.DELIVERY_MENU]}`,
        value: DeepLinkDestinations.DELIVERY_MENU
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.USER_DETAILS]}`,
        value: DeepLinkDestinations.USER_DETAILS
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.USER_REWARDS]}`,
        value: DeepLinkDestinations.USER_REWARDS
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.USER_ORDERS]}`,
        value: DeepLinkDestinations.USER_ORDERS
    },
    {
        label: `${DeepLinkDestinationsLabels[DeepLinkDestinations.USER_PAYMENT_METHODS]}`,
        value: DeepLinkDestinations.USER_PAYMENT_METHODS
    }
];

export const DeepLinkCustomPaths = {
    [DeepLinkDestinations.START_OF_JOURNEY]: ':location_id/journey',
    [DeepLinkDestinations.PRE_ORDER_MENU]: ':location_id/po',
    [DeepLinkDestinations.ORDER_TO_TABLE_MENU]: ':location_id/ott',
    [DeepLinkDestinations.PAT_PAY_MENU]: ':location_id/pat/pay',
    [DeepLinkDestinations.DINE_IN_MENU]: ':location_id/di',
    [DeepLinkDestinations.DELIVERY_MENU]: ':location_id/delivery',
    [DeepLinkDestinations.USER_DETAILS]: ':location_id/link/user/details',
    [DeepLinkDestinations.USER_REWARDS]: ':location_id/loyalty/user/rewards',
    [DeepLinkDestinations.USER_ORDERS]: ':location_id/link/user/activity',
    [DeepLinkDestinations.USER_PAYMENT_METHODS]: ':location_id/link/user/payment-methods'
};

export const replaceDeepLinkCustomPath = (customPath: string, locationId: string) =>
    customPath.replace(':location_id', locationId);

export const getDeepLinkDestinationFromCustomPath = (
    customPath?: string
): DeepLinkDestinations | undefined => {
    if (!customPath) {
        return undefined;
    }
    const pathSegments = customPath.split('/');
    const entries = Object.entries(DeepLinkCustomPaths) as [DeepLinkDestinations, string][];
    const match = entries.find(([, template]) => {
        const templateSegments = template.split('/');
        if (templateSegments.length !== pathSegments.length) {
            return false;
        }
        return templateSegments.every(
            (segment, index) => segment.startsWith(':') || segment === pathSegments[index]
        );
    });
    return match?.[0];
};

export type ICreateTrackingLink = Omit<
    ITrackingLink,
    '_id' | 'id' | 'createdAt' | 'updatedAt' | 'status' | 'clicks' | 'shortLinkId' | 'isActive' | 'isArchived'
>;

export interface ITrackingLinksResponse {
    items: ITrackingLink[];
    page: PageObject;
}
export enum TrackingLinkType {
    // For now, it's only used for web app links
    EXTERNAL_LINK = 'EXTERNAL_LINK',
    APP_LINK = 'APP_LINK'
}

export const TrackingLinkTypeLabels = {
    [TrackingLinkType.EXTERNAL_LINK]: 'Web App',
    [TrackingLinkType.APP_LINK]: 'Dynamic'
};

export const TRACKING_LINK_CLICKS_MEASURE = 'link_metrics.click_count';
export const TRACKING_LINK_SHORT_LINK_ID_DIMENSION = 'link_metrics.short_link_id';
